import {defineStore} from "pinia";
import {groupBy, parseInt} from "lodash";
import api from "../api";
import {toastStore} from "./ToastStore";

export const useCartStore = defineStore("CartStore", {
    state: () => {

        return {
            articles: [],
            articleSets: [],
            services: [],
            event: [],
            order: [],
            auth: false,
            isLoggedIn: false,
            isDiscounted: false,
            role: null,
        };
    },
    persist: true,

    getters: {
        count: (state) => {

            return state.articles.length + state.articleSets.length + state.services.length;
        },

        countArticlesInCart: (state) => {
            return state.articles.length + state.articleSets.length;
        },

        countArticles: (state) => {

            return state.articles.length;
        },

        countArticleSets: (state) => {

            return state.articleSets.length;
        },

        countServices: (state) => {

            return state.services.length;
        },

        isEmpty: (state) => state.count === 0,

        noArticles: (state) => state.countArticlesInCart === 0,

        noServices: (state) => state.countServices === 0,

        groupedArticles: (state) => {
            const grouped = groupBy(
                state.articles,
                (article) => article.slug
            );
            const sorted = Object.keys(grouped).sort();

            let inOrder = {};

            sorted.forEach((key) => (inOrder[key] = grouped[key]));

            return inOrder;
        },

        groupedArticleSets: (state) => {
            const grouped = groupBy(
                state.articleSets,
                (articleSet) => articleSet.slug
            );
            const sorted = Object.keys(grouped).sort();

            let inOrder = {};

            sorted.forEach((key) => (inOrder[key] = grouped[key]));

            return inOrder;
        },

        groupArticleCount: (state) => (slug) =>
            state.groupedArticles[slug].length,

        groupArticleSetCount: (state) => (slug) =>
            state.groupedArticleSets[slug].length,

        total: (state) => {
            const articlesTotal = state.articles.reduce(
                (p, c) => parseFloat(p) + parseFloat(c.rentalprice),
                0
            );
            const articleSetsTotal = state.articleSets.reduce(
                (p, c) => parseFloat(p) + parseFloat(c.price),
                0
            );
            return parseFloat(articlesTotal) + parseFloat(articleSetsTotal);
        },

        totalDiscount: (state) => {
            let discount = 0;
            const articlesTotalDiscount = state.articles.reduce(
                (p, c) => parseFloat(p) + parseFloat(c.rentalprice_discounted),
                0
            );
            const articleSetsTotalDiscount = state.articleSets.reduce(
                (p, c) => parseFloat(p) + parseFloat(c.rentalprice_discounted),
                0
            );
            return (parseFloat(articlesTotalDiscount) + parseFloat(articleSetsTotalDiscount));
        },

        tax() {
            let total = this.total;
            if ((this.total - this.totalDiscount) < this.total) {
                total = this.total - ((this.total - this.totalDiscount));
            }
            return total * (8.1 / 100);
        },

        totalTax() {
            let total = this.total;
            if ((this.total - this.totalDiscount) < this.total) {
                total = this.total - ((this.total - this.totalDiscount));
            }
            return this.tax + total;
        },
    },

    actions: {
        addArticles(count, article, toast = true) {
            count = parseInt(count);
            for (let index = 0; index < count; index++) {
                this.articles.push({...article});
            }
            if (toast) {
                toastStore.setMessage('Der Artikel wurde in den Warenkorb gelegt.')
                toastStore.setIsOpened(true);
            }
        },

        addArticleSets(count, articleSet, toast = true) {
            count = parseInt(count);
            for (let index = 0; index < count; index++) {
                this.articleSets.push({...articleSet});
            }
            if (toast) {
                toastStore.setMessage('Das Artikelset wurde in den Warenkorb gelegt.')
                toastStore.setIsOpened(true);
            }
        },

        addService(count, service) {
            if (this.services.filter(
                (addedService) => addedService.slug === service.slug
            ).length > 0) {
                toastStore.setMessage('Sie haben den Service bereits im Warenkorb.');
            } else {
                this.services.push({...service});
                toastStore.setMessage('Der Service wurde in den Warenkorb gelegt.')
            }
            toastStore.setIsOpened(true);
        },

        clearArticle(articleSlug) {
            this.articles = this.articles.filter(
                (article) => article.slug !== articleSlug
            );
        },

        clearArticleSet(articleSetSlug) {
            this.articleSets = this.articleSets.filter(
                (articleSet) => articleSet.slug !== articleSetSlug
            );
        },

        clearService(serviceSlug) {
            this.services = this.services.filter(
                (service) => service.slug !== serviceSlug
            );
        },

        setArticleCount(article, count) {
            this.clearArticle(article.slug);
            this.addArticles(count, article);
        },

        setArticleSetCount(articleSet, count) {
            this.clearArticleSet(articleSet.slug);
            this.addArticleSets(count, articleSet);
        },

        async setDiscount() {
            // fetch articles with discount from backend
            // Berechnung des Rabatts, wenn Anmeldung nach Füllen des Warenkorbs erfolgt

            api.post("/api/articles/getDiscounts", {
                groupedArticles: this.groupedArticles,
                groupedArticleSets: this.groupedArticleSets,
            }).then((response) => {
                if (response.data) {
                    if (Array.isArray(response.data.articleSets)) {
                        response.data.articleSets.forEach((articleSet) => {
                            console.log('clearArticleSet');
                            if (this.clearArticleSet && this.addArticleSets) {
                                this.clearArticleSet(articleSet.slug);
                                this.addArticleSets(articleSet.count, articleSet, false);
                            }
                        });
                    }
                    if (Array.isArray(response.data.articles)) {
                        response.data.articles.forEach((article) => {
                            console.log('clearArticle');
                            if (this.clearArticle && this.addArticles) {
                                this.clearArticle(article.slug);
                                this.addArticles(article.count, article, false);
                            }
                        });
                    }
                    this.isDiscounted = true;
                }
            })
                .catch(function (error) {
                    console.log(error);
                });
        },
        async checkLoginStatus() {
            this.auth = (await api.get('/api/users/checkStatus')).data;
            this.isLoggedIn = !!this.auth.authenticated;
            this.role = this.auth.role;

            if (this.auth.activeOrder) {
                this.activeOrder = this.auth.activeOrder.id;
            }

            if (this.isLoggedIn && !this.isDiscounted) {
                this.setDiscount();
            }
        },
    },
});
